<script lang="ts" setup>
import type { PickUpPointType } from '~/api/common';
import OrderAPI from '~/api/order';

const TEST_ID = 'order-pickup-points-form';

const { t } = useI18n();
const orderStore = useOrderStore();
const modalStore = useModalStore();

const isVisibleMapModal = ref(false);
const selectedPickUpPoint = ref<PickUpPointType>();

const { execute, isLoading } = useRequest(OrderAPI.selectDelivery, () => ({
  body: { type: 'pvz', pvzId: selectedPickUpPoint.value?.id },
}));
const state = computed(() => {
  if (isLoading.value) return 'loading';
  if (selectedPickUpPoint) return 'selected';
  return 'none';
});

const selectPickUpPoint = (value: PickUpPointType) => {
  isVisibleMapModal.value = false;
  selectedPickUpPoint.value = value;
  execute()
    ?.then(orderStore.updateOrderState)
    .catch(() => {
      modalStore.showError({
        title: t('message.error'),
        action: t('message.action'),
      });
    });
};

const emit = defineEmits<{
  (event: 'select', value: PickUpPointType): void;
}>();

watch(
  () => orderStore.delivery.current?.pvz,
  (v) => {
    if (v) {
      selectedPickUpPoint.value = v;
      // TODO: подумать над улучшением defineModal?
      emit('select', selectedPickUpPoint.value);
    }
  },
  { immediate: true },
);
</script>

<template>
  <BaseToggleTransition :state="state" :data-test-id="TEST_ID">
    <BaseLoader v-if="state === 'loading'" dark area />
    <div
      v-if="selectedPickUpPoint && state === 'selected'"
      class="orderPickupPointsForm__result"
    >
      <p class="orderPickupPointsForm__description">
        {{ t('description', selectedPickUpPoint) }}
      </p>
      <button
        @click="isVisibleMapModal = true"
        class="orderPickupPointsForm__change"
      >
        {{ t('change') }}
      </button>
    </div>
    <BaseButton
      v-else
      @click="isVisibleMapModal = true"
      dark
      :data-test-id="`${TEST_ID}-select`"
    >
      {{ t('select') }}
    </BaseButton>
  </BaseToggleTransition>

  <OrderMapModal
    v-model:visible="isVisibleMapModal"
    @select="selectPickUpPoint"
  />
</template>

<i18n>
ru:
  description: |
    Выбран пункт {service}:
    {address}
    Код ПВЗ: {pvzcode}

    Вы получите СМС, когда заказ поступит в пункт.
  change: Изменить
  select: Выбрать на карте
  message:
    error: Во время выбора способа получения произошла ошибка
    action: Вернуться

en:
  description: |
    Selected point {service}:
    {address}
    PVZ code: {pvzcode}

    You will receive an SMS when the order arrives at the point.
  change: Change
  select: Select on map
  message:
    error: An error occurred while choosing the method of obtaining
    action: Return
</i18n>

<style lang="scss">
.orderPickupPointsForm {
  &__result {
    padding-bottom: 8px;
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }

  &__description {
    font-size: 14px;
    line-height: 17px;
    letter-spacing: 0.2px;
    color: #121212;
    margin: 0;
    white-space: pre-wrap;
  }

  &__change {
    font-size: 9px;
    height: 14px;
    line-height: 14px;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: #c0c0c0;
    transition: color 0.3s ease;
    background: none;
    padding: 0;
    border: 0;
    cursor: pointer;
    margin-left: auto;

    &:hover {
      color: #121212;
    }
  }
}
</style>
